/**
 * <ActivityCopyBar> — la copie de l'élève, en bas de l'écran d'une activité (organism).
 *
 * La « barre de copie » (forme A, décidée par Wajih le 15/10) : posée en bas comme le composeur
 * d'une conversation, pendant que l'énoncé défile au-dessus. Elle rassemble ce que l'ancien code
 * cachait derrière un gros bouton noir (« scanner mon travail ») puis dans la boîte « Mon travail » :
 *  - les vignettes des pages déjà envoyées : un appui ouvre la page en grand (avec « Supprimer »,
 *    c'est l'écran qui monte la visionneuse) ; au-delà de ce qui tient, la dernière dit « +N », et
 *    les pages en échec passent devant (`copyBarThumbs`) : jamais une page à renvoyer derrière « +N » ;
 *  - l'appareil photo, rond : il prend la page suivante (une page par prise, comme aujourd'hui) ;
 *  - « J'ai fini », le bouton de l'univers : rendre la copie, en un geste.
 *
 * Un geste à la fois (Wajih, 16/10) : tant qu'aucune page n'est envoyée, la barre n'est qu'UN
 * bouton, plein, à toute la largeur, « Prendre ma copie en photo » ; « J'ai fini » n'existe pas
 * encore (caché, pas grisé). Dès la première page, la barre prend sa forme : vignettes, appareil
 * photo rond, « J'ai fini ». La consigne de l'étape photo n'y tient plus : le bouton dit le geste,
 * la colonne du grand écran l'écrit.
 *
 * Une activité orale rend un enregistrement : « Enregistrer mon travail » est le seul bouton, puis
 * la pilule de l'enregistrement (le micro et « 2 min 14 s ») prend la place des vignettes, le micro
 * celle de l'appareil photo. L'enregistreur reste l'ancien.
 *
 * Contrôlée : les pages, leur envoi et la copie qui part arrivent en props ; chaque geste remonte.
 * Elle ne dessine que sa surface et son filet ; l'écran la pose en bas, dans le flux (jamais par-
 * dessus la fin de l'énoncé). Sur un grand écran, la copie passe en colonne (`<ActivityCopyColumn>`).
 */
import React from "react"
import { View, type LayoutChangeEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatMinutesSeconds } from "../../../ports/primary/ui/formatters/dateFormatter"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { MediaThumbnail } from "../../atoms/MediaThumbnail"
import { Row } from "../../atoms/containers/Row"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { type UICopyPhoto, type UICopyRecording } from "../../../ports/primary/ui/types/studentActivity"
import { copyBarThumbs } from "./copyBarThumbs"

// Une page de la copie et l'enregistrement : leurs formes vivent dans `ports/primary/ui/types`.
export type { UICopyPhoto, UICopyRecording }

/** Ce que l'activité rend : des photos de la copie, ou un enregistrement. */
export type ActivityCopyMedium = "photo" | "audio"

/** Ce que la barre et la colonne de copie partagent : la copie, et les gestes qui la font. */
export type ActivityCopyProps = {
    /** Défaut `photo`. */
    medium?: ActivityCopyMedium
    /** Les pages déjà prises, dans l'ordre. */
    photos: UICopyPhoto[]
    /** L'enregistrement d'une activité orale, une fois fait. */
    recording?: UICopyRecording | null
    /**
     * La consigne de l'étape de rendu (« Prends chaque page en photo… »). La colonne l'écrit (absente :
     * la phrase par défaut) ; la barre, réduite au geste, ne l'écrit pas.
     */
    instruction?: string
    /** Une page touchée : l'écran l'ouvre en grand. */
    onOpenPhoto: (index: number) => void
    /** Une page qui n'est pas partie, touchée : l'écran la renvoie. Absent : elle s'ouvre comme les autres. */
    onRetryPhoto?: (photo: UICopyPhoto) => void
    /** L'appareil photo (ou le micro) : l'écran ouvre le scanner (ou l'enregistreur). */
    onCapture: () => void
    /** L'enregistrement touché : l'écran rouvre l'enregistreur. */
    onOpenRecording?: () => void
    /**
     * L'enregistrement se réécoute avant d'être rendu (l'ancienne boîte « Mon travail ») : la pilule
     * le joue et le met en pause, au lieu de rouvrir l'enregistreur. Absent : comme avant.
     */
    playback?: { playing: boolean; onTogglePlay: () => void }
    /**
     * « Réenregistrer », une fois l'enregistrement d'une activité orale fait : l'écran le jette et
     * rouvre l'enregistreur. Absent : le micro rouvre l'enregistreur (`onCapture`), comme avant.
     */
    onRerecord?: () => void
    /** « J'ai fini » : l'écran rend la copie. */
    onFinish: () => void
    /** La copie part : « J'ai fini » tourne, plus rien ne presse. */
    finishing?: boolean
    /**
     * « J'ai fini » ne se presse pas encore (une page part, ou n'est pas partie) : grisé, les autres
     * gestes restent. C'est l'écran qui en décide. Défaut `false`.
     */
    finishDisabled?: boolean
    testID?: string
}

export type ActivityCopyBarProps = ActivityCopyProps & {
    /**
     * La largeur de la colonne de lecture où l'énoncé se pose, marges comprises : la rangée s'y aligne,
     * la barre garde toute la largeur. Absente (défaut) : la rangée prend la barre.
     */
    column?: number
}

/** La taille d'une vignette de la barre : celle d'une pièce dans le composeur, sous le pouce. */
export const COPY_BAR_THUMB_SIZE = 48
/**
 * Les largeurs de barre où trois, puis deux vignettes tiennent à côté des deux boutons ; en deçà, une.
 * La seconde est aussi celle où le bouton seul garde son glyphe sans que son libellé passe à la ligne.
 */
const THREE_THUMBS_FROM = 360
const TWO_THUMBS_FROM = 320

export const COPY_TEXTS = {
    photoHint: "Prends ta copie en photo, page par page.",
    audioHint: "Enregistre ton travail.",
    takeFirst: "Prendre ma copie en photo",
    takeNext: "Ajouter une photo",
    record: "Enregistrer mon travail",
    recording: (duration: string) => `Enregistrement · ${duration}`,
    listen: (duration: string) => `Écouter l'enregistrement · ${duration}`,
    pause: "Mettre en pause l'enregistrement",
    page: (n: number) => `Page ${n} de ta copie`,
    pageFailed: (n: number) => `Page ${n} non envoyée, réessayer`,
    finish: "J'ai fini",
    rerecord: "Réenregistrer",
}

/** Ce que la pilule de l'enregistrement dit au lecteur d'écran : l'écouter, la mettre en pause, ou la rouvrir. */
export function recordingLabel(seconds: number, playback: ActivityCopyProps["playback"]): string {
    const duration = formatMinutesSeconds(seconds)
    if (playback == null) return COPY_TEXTS.recording(duration)
    return playback.playing ? COPY_TEXTS.pause : COPY_TEXTS.listen(duration)
}

/** La pilule de l'enregistrement : son glyphe, son geste, et quand elle ne presse pas. */
export function recordingPress(
    playback: ActivityCopyProps["playback"],
    onOpenRecording: ActivityCopyProps["onOpenRecording"],
    finishing: boolean
) {
    return {
        iconName: playback == null ? "microphone" : playback.playing ? "pause" : "play",
        onPress: playback?.onTogglePlay ?? onOpenRecording,
        disabled: (playback == null && onOpenRecording == null) || finishing,
    } as const
}

/**
 * Combien de vignettes tiennent dans une barre mesurée (`onLayout`) : trois dès `threeFrom`, deux
 * dès `twoFrom`, une en deçà. `roomy` : la barre en tient deux.
 */
export function useCopyBarThumbSlots(threeFrom: number, twoFrom: number) {
    const three = useWidthAtLeast(threeFrom)
    const two = useWidthAtLeast(twoFrom)
    const onLayout = (event: LayoutChangeEvent) => {
        three.onLayout(event)
        two.onLayout(event)
    }
    return { onLayout, maxThumbs: three.atLeast ? 3 : two.atLeast ? 2 : 1, roomy: two.atLeast }
}

/** La pile de gestes d'une page : la renvoyer si elle n'est pas partie, sinon l'ouvrir. */
export function copyPhotoPress(props: ActivityCopyProps, photo: UICopyPhoto, index: number): () => void {
    if (photo.status === "failed" && props.onRetryPhoto != null) return () => props.onRetryPhoto!(photo)
    return () => props.onOpenPhoto(index)
}

export function ActivityCopyBar(props: ActivityCopyBarProps) {
    const {
        medium = "photo",
        photos,
        recording,
        onCapture,
        onOpenRecording,
        playback,
        onRerecord,
        onFinish,
        finishing = false,
        finishDisabled = false,
        column,
        testID,
    } = props
    const { onLayout, maxThumbs, roomy } = useCopyBarThumbSlots(THREE_THUMBS_FROM, TWO_THUMBS_FROM)
    // La rangée sur la colonne de l'énoncé : ses marges sont celles de la barre.
    const rowStyle = column != null ? styles.column(column) : undefined

    const audio = medium === "audio"
    const hasWork = audio ? recording != null : photos.length > 0
    // Une page en échec n'est jamais cachée derrière « +N » : c'est en la touchant qu'on la renvoie.
    const visible = copyBarThumbs(photos, maxThumbs)
    const more = photos.length - visible.length
    // Une activité orale enregistrée : le micro réenregistre, quand l'écran le propose.
    const rerecords = audio && onRerecord != null

    if (!hasWork) {
        return (
            <View style={styles.bar} onLayout={onLayout} testID={testID}>
                <View style={rowStyle}>
                    <Button
                        iconName={roomy ? (audio ? "microphone" : "camera") : undefined}
                        label={audio ? COPY_TEXTS.record : COPY_TEXTS.takeFirst}
                        onPress={onCapture}
                        fullWidth
                    />
                </View>
            </View>
        )
    }

    return (
        <View style={styles.bar} onLayout={onLayout} testID={testID}>
            <Row gap="3" align="center" style={rowStyle}>
                <View style={styles.work}>
                    {audio && recording != null && (
                        <Button
                            variant="outline"
                            size="sm"
                            {...recordingPress(playback, onOpenRecording, finishing)}
                            // Sous le pouce, la durée seule : le micro dit déjà que c'est l'enregistrement.
                            label={formatMinutesSeconds(recording.durationSeconds)}
                            accessibilityLabel={recordingLabel(recording.durationSeconds, playback)}
                        />
                    )}
                    {!audio && (
                        <Row gap="2" align="center">
                            {visible.map(({ photo, index }, position) => {
                                const last = position === visible.length - 1
                                const failed = photo.status === "failed"
                                return (
                                    <MediaThumbnail
                                        key={photo.id}
                                        uri={photo.uri}
                                        size={COPY_BAR_THUMB_SIZE}
                                        dimmed={photo.status === "sending"}
                                        failed={failed}
                                        moreCount={last ? more : 0}
                                        onPress={finishing ? undefined : copyPhotoPress(props, photo, index)}
                                        accessibilityLabel={
                                            failed ? COPY_TEXTS.pageFailed(index + 1) : COPY_TEXTS.page(index + 1)
                                        }
                                    />
                                )
                            })}
                        </Row>
                    )}
                </View>

                <IconButton
                    iconName={rerecords ? "redo" : audio ? "microphone" : "camera"}
                    size={44}
                    iconSize={20}
                    color="role-student-tint"
                    iconColor="role-student-tint-ink"
                    onPress={rerecords ? onRerecord : onCapture}
                    disabled={finishing}
                    accessibilityLabel={
                        rerecords ? COPY_TEXTS.rerecord : audio ? COPY_TEXTS.record : COPY_TEXTS.takeNext
                    }
                />
                <Button
                    label={COPY_TEXTS.finish}
                    onPress={onFinish}
                    loading={finishing}
                    disabled={finishDisabled && !finishing}
                />
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La surface d'un composeur : la carte, un filet au-dessus. */
    bar: {
        backgroundColor: theme.colors["surface-raised"],
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
        paddingHorizontal: theme.spacing["4"],
        paddingVertical: theme.spacing["3"],
    },
    work: { flex: 1, minWidth: 0, alignItems: "flex-start" },
    column: (width: number) => ({
        width: "100%" as const,
        maxWidth: width - 2 * theme.spacing["4"],
        alignSelf: "center" as const,
    }),
}))
